// Sorcery card art is portrait (63:88) with chrome at both ends and
// down both sides: the name banner plus cost / threshold icons across
// the top (~2.5%-9.5% of card height), the type-line banner, rules box
// and artist credit across the bottom (from ~71.5% down), and ~3% of
// frame on each side. Profile pictures should show only the
// illustration between them.
//
// Measured across Alpha, Beta, Arthurian Legends, Gothic and Dragonlord
// printings: banner bottom edge 9.4-9.8%, type-line top edge 71.7%
// (long rules text) to 75.5% (Avatars), inner frame edge 2.9-3.2% per
// side. The window is padded to stay clear of all of them.
export const AVATAR_ART_WINDOW = { left: 0.03, right: 0.97, top: 0.10, bottom: 0.70 };

const CARD_ASPECT = 88 / 63;

// Inline style for an <img> absolutely positioned inside a SQUARE
// `position: relative; overflow: hidden` box so the box shows exactly
// the art window: the image is scaled until the window covers the box
// on both axes, then offset so the window's centre sits at the box's
// centre. Percentages are relative to the box, which is why the box
// must be square. Use <AvatarImage> (src/components/ui/AvatarImage.jsx)
// rather than applying this by hand.
export const AVATAR_ART_IMG_STYLE = (() => {
  const { left, right, top, bottom } = AVATAR_ART_WINDOW;
  const width = Math.max(1 / (right - left), 1 / ((bottom - top) * CARD_ASPECT));
  const height = width * CARD_ASPECT;
  const pct = (v) => `${(v * 100).toFixed(2)}%`;
  return {
    position: 'absolute',
    width: pct(width),
    height: pct(height),
    maxWidth: 'none',
    left: pct(0.5 - (width * (left + right)) / 2),
    top: pct(0.5 - (height * (top + bottom)) / 2),
    objectFit: 'cover',
  };
})();

// Resolve a player's chosen avatar (a sorcery card id) to an image URL.
//
// Field name history: the local profile, the `/profile/me` payload, and
// the matchmaking socket event all use `profileAvatar`. Some earlier
// friend-related code read a shorter `avatar` field instead, and the
// silent fallback to `null` caused remote players' avatars to vanish
// across the friends sidebar and the friend profile overlay. Reading
// both names here keeps the UI robust to either payload shape. If the
// server ever renames the field we won't break again.
export function getAvatarCardId(playerOrProfile) {
  if (!playerOrProfile) return null;
  return playerOrProfile.profileAvatar ?? playerOrProfile.avatar ?? null;
}

/**
 * Resolve a player object (friend, public profile, self profile) to a
 * card image URL via the loaded sorcery card database. Returns null if
 * the player has no avatar set or the card isn't in the index.
 */
export function resolveAvatarUrl(playerOrProfile, sorceryCards) {
  const cardId = getAvatarCardId(playerOrProfile);
  if (!cardId || !sorceryCards) return null;
  const card = sorceryCards.find((c) => c.unique_id === cardId);
  return card?.printings?.[0]?.image_url || null;
}

/**
 * Resolve a deck (spawnedDecks slot shape: { cards: [{ cardId }] })
 * to its Avatar card's display identity. Returns { name, url } for
 * the first Avatar in the deck, or null when the deck has none /
 * the card database isn't loaded.
 */
export function resolveDeckAvatar(deck, sorceryCards) {
  if (!deck?.cards || !Array.isArray(sorceryCards)) return null;
  for (const entry of deck.cards) {
    const card = sorceryCards.find((c) => c.unique_id === entry?.cardId);
    if (card?.type === 'Avatar') {
      return { name: card.name || null, url: card.printings?.[0]?.image_url || null };
    }
  }
  return null;
}
